import { observer } from 'mobx-react-lite';
import { format } from 'date-fns';
import noun from 'plural-ru';
import CloseIcon from '../assets/close-icon.svg?react';
import './custom-slider.css';
import { ru } from 'date-fns/locale';
import { useNightsCounter } from '../utils/useNightsCounter';
import BinIcon from '../assets/bin-icon.svg?react';
import { Button } from '../components/shared/Button/Button';
import { SERVICE_PRICE_TYPE } from '../apiMethods/getServices';
import { ServiceStore } from '../stores/serviceStore';
import { formatMoney } from '../utils/formatMoney';

type SummaryParams = {
  adults: number;
  childs: number;
  from?: Date;
  to?: Date;
  roomName?: string;
  rateName?: string;
  roomsCount?: number;
  totalPrice: number | null;
  priceChange?: number | null;
  priceChangeSign?: string;
};

const useGuestsCounter = (adults: number, childs: number) => {
  const adultsNoun = noun(adults, 'взрослый', 'взрослых', 'взрослых');
  const childsNoun = noun(childs, 'ребёнок', 'ребёнка', 'детей');

  return `${adults} ${adultsNoun}` + (childs ? `, ${childs} ${childsNoun}` : '');
};

const formatDate = (date?: Date) => (date ? format(date, 'd MMMM yyyy', { locale: ru }) : '-');

const InfoRow = ({
  label,
  value,
  isTotal,
}: {
  label: string;
  value: string;
  isTotal?: boolean;
}) => (
  <div className={`my-4 grid grid-cols-2 items-start font-light `}>
    <span className={isTotal ? 'font-medium' : ''}>{label}</span>
    <span className={isTotal ? 'font-normal' : ''}>{value}</span>
  </div>
);

const ServiceInfoRow = ({
  label,
  value,
  onRemove,
}: {
  label: string;
  value: string;
  onRemove: () => void;
}) => {
  return (
    <div className="flex flex-row items-start justify-between">
      <div className="flex flex-col pr-4 font-light">
        <span className="text-neutral-900">{label}</span>
        <span className="text-sm text-neutral-200">{value}</span>
      </div>
      <Button variant="link" style="secondary" onClick={onRemove}>
        <BinIcon />
      </Button>
    </div>
  );
};

type OrderSummaryProps = {
  onClose?: () => void;
  serviceStore?: ServiceStore;
  showServices?: boolean;
  params: SummaryParams;
};

export const OrderSummary = observer(
  ({ onClose, serviceStore, showServices, params }: OrderSummaryProps) => {
    const {
      adults,
      childs,
      from,
      to,
      roomName,
      roomsCount,
      rateName,
      totalPrice,
      priceChange,
      priceChangeSign,
    } = params;

    const nightsStat = useNightsCounter({ from, to });
    const guestsStat = useGuestsCounter(adults ?? 0, childs ?? 0);

    const onRemoveService = (serviceId: number, subServiceId?: number) => {
      const service = serviceStore?.serviceList.getItem(serviceId);

      if (service?.price_type === SERVICE_PRICE_TYPE.FIX_PRICE) {
        service &&
          serviceStore?.toggleService({
            serviceId: service.id,
            name: service.name,
            price: service.price,
            quantity: 1,
          });
      }

      if (service?.price_type === SERVICE_PRICE_TYPE.SUB_SERVICE_PRICE) {
        service &&
          serviceStore?.toggleSubService({
            serviceId,
            subServiceId,
            price: 0,
            name: '',
            quantity: 1,
          });
      }

      if (service?.price_type === SERVICE_PRICE_TYPE.PER_NIGTH) {
        serviceStore?.toggleQuantityService({
          serviceId,
          quantity: 0,
          name: service.name,
          price: service.price,
          priceAnnotation: '',
        });
      }
    };

    return (
      <>
        <div className="flex items-center justify-between">
          <h4 className="mt-6 text-2xl leading-[32px]">Информация о заказе</h4>
          <CloseIcon className="xl:hidden" onClick={onClose} />
        </div>
        <section>
          <h6 className="mt-11 text-[18px] leading-[28px]">Параметры</h6>
          <InfoRow label="Прибытие" value={formatDate(from)} />
          <InfoRow label="Выезд" value={formatDate(to)} />
          <InfoRow label="Проживание" value={nightsStat} />
          <InfoRow label="Гости" value={guestsStat} />
          <h6 className="mt-11 text-[18px] leading-[28px]">Номер</h6>
          <InfoRow label="Категория" value={roomName || '-'} />
          <InfoRow label="Количество" value={String(roomsCount || '-')} />
          {rateName && <InfoRow label="Тариф" value={rateName} />}
          {showServices && (serviceStore?.selectedServices.length ?? 0) > 0 && (
            <div className="space-y-4">
              <h5 className="mt-11 text-xl leading-[30px]">Дополнительные услуги</h5>
              {serviceStore?.selectedServices.map(service => (
                <ServiceInfoRow
                  key={`${service.serviceId}-${service.subServiceId}`}
                  label={service.name}
                  value={`${service.price * service.quantity}р ${service.priceAnnotation ? '/ ' + service.priceAnnotation : ''}`}
                  onRemove={() => onRemoveService(service.serviceId, service.subServiceId)}
                />
              ))}
            </div>
          )}
          {totalPrice && (
            <>
              <div className="-mx-8 my-10 border-b border-neutral-40"></div>
              {priceChange != undefined && (
                <InfoRow
                  label="Изменение стоимости"
                  value={`${priceChangeSign} ${formatMoney(Math.abs(priceChange))} ${String.fromCharCode(8381)} `}
                />
              )}
              <InfoRow
                label="Всего"
                value={`${formatMoney(totalPrice)} ${String.fromCharCode(8381)} / ${nightsStat}`}
                isTotal
              />
            </>
          )}
        </section>
      </>
    );
  },
);
